import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import IDCameraPage from 'src/products/general/components/organisms/IDCameraPage/IDCameraPage'
import OverlaySimple from 'src/designSystem/components/organisms/Overlay/variants/OverlaySimple'
import {IDScanProcessingStatuses, IDScanProps} from 'src/products/loans/IDScan/IDScan.types'
import {BottomSheetOrOverlay} from 'src/designSystem/components/organisms/BottomSheetOrOverlay/BottomSheetOrOverlay'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import Box from 'src/designSystem/components/atoms/Box/Box'
import Check from 'src/assets/icons/Check.svg'
import PFSvg from 'src/designSystem/components/atoms/PFSvg/PFSvg'
import {Loading} from 'src/designSystem/components/atoms/Loading/Loading'
import {PFUnorderedList} from 'src/designSystem/components/molecules/PFUnorderedList/PFUnorderedList'
import {largeGap, tinyGap} from 'src/designSystem/layout'
import {Consumer} from '@possible/cassandra'
import {AnalyzeIdDocumentFailureCodes} from 'src/cassandra'

export const AcceptableDocumentTypeNotice: FC = () => {
  const {t} = useTranslation('IDScan')
  return (
    <Box>
      <PFText variant={'label_md'}>{t('InfoBoxTitle')}</PFText>
      <PFUnorderedList
        items={[
          {value: t('InfoBoxOption1'), key: '1'},
          {value: t('InfoBoxOption2'), key: '2'},
        ]}
      />
    </Box>
  )
}

type IDScanCaptureTemplateProps = Omit<IDScanProps, 'phase'>

export const IDScanCaptureTemplate: FC<IDScanCaptureTemplateProps> = (props) => {
  const {
    onImageSubmitStart: handleOnSubmitStart,
    onImageSubmitComplete: handleOnSubmitComplete,
    onContinue: handleOnContinue,
    error,
    processingStatus,
    clearErrors: handleClearErrors,
    busy: isBusy,
  } = props
  const {t} = useTranslation(['IDScan', 'Common'])

  const BottomSheetContent: {[key in IDScanProcessingStatuses]: JSX.Element} = {
    [IDScanProcessingStatuses.Processing]: (
      <Box>
        <Box height={largeGap} width={largeGap} marginBottom={tinyGap}>
          <Loading type="loader0" size="large" />
        </Box>
        <PFText variant="h3">{t('ProcessingStatusTitle')}</PFText>
        <PFText variant="p">{t('ProcessingStatusMessage')}</PFText>
      </Box>
    ),
    [IDScanProcessingStatuses.Complete]: (
      <>
        <Box direction="column" justify="start">
          <PFSvg
            SvgUri={Check}
            svgProps={{width: 30, height: 30}}
            manualWebResizeMethod={'width'}
          />
          <PFText variant="h3">{t('CompletedStatusTitle')}</PFText>
        </Box>
      </>
    ),
  }

  const handleOnOk: (() => Promise<void>) | undefined =
    processingStatus === IDScanProcessingStatuses.Complete ? handleOnContinue : undefined

  return (
    <>
      <IDCameraPage
        onSubmissionStart={handleOnSubmitStart}
        onComplete={handleOnSubmitComplete}
        lowerInstructionMessage={<AcceptableDocumentTypeNotice />}
        // By default documentSubType === 'Other'. It will be set to the correct type when the image is analyzed on the BE
        defaultDocumentSubType={Consumer.types.UserDocumentSubType.Other}
      />
      <OverlaySimple
        visible={error === AnalyzeIdDocumentFailureCodes.Expired}
        title={t('ExpiredTitle')}
        text={t('ErrorExpiredMessage')}
        testID="IDScanCaptureTemplate-ExpiredOverlay"
        okayButtonText={t('Common:Retry')}
        onOkay={handleClearErrors}
      />
      <OverlaySimple
        visible={error === AnalyzeIdDocumentFailureCodes.InvalidDocumentType}
        title={t('InvalidDocumentTypeTitle')}
        text={t('InvalidDocumentTypeMessage')}
        testID="IDScanCaptureTemplate-InvalidDocumentTypeOverlay"
        okayButtonText={t('Common:Retry')}
        onOkay={handleClearErrors}
      />
      <OverlaySimple
        visible={error === AnalyzeIdDocumentFailureCodes.IdUserDetailsMismatch}
        title={t('IDUserDetailsMismatchTitle')}
        text={t('IDUserDetailsMismatchMessage')}
        testID="IDScanCaptureTemplate-IDUserDetailsMismatchOverlay"
        okayButtonText={t('Common:Retry')}
        onOkay={handleClearErrors}
      />
      <OverlaySimple
        visible={error === AnalyzeIdDocumentFailureCodes.PoorImageQuality}
        title={t('PoorImageQualityTitle')}
        text={t('PoorImageQualityMessage')}
        testID="IDScanCaptureTemplate-PoorImageQualityOverlay"
        okayButtonText={t('Common:Retry')}
        onOkay={handleClearErrors}
      />
      <OverlaySimple
        visible={error === AnalyzeIdDocumentFailureCodes.OtherError}
        title={t('OtherErrorTitle')}
        text={t('OtherErrorMessage')}
        testID="IDScanCaptureTemplate-OtherErrorOverlay"
        okayButtonText={t('Common:Retry')}
        onOkay={handleClearErrors}
      />
      <BottomSheetOrOverlay
        titleVariant="h3_regular"
        showCloseButton={false}
        visible={processingStatus !== undefined}
        onDismiss={(): void => {}}
        okayButtonText={
          processingStatus === IDScanProcessingStatuses.Complete ? t('Common:Continue') : undefined
        }
        okayButtonDisabled={isBusy}
        onOkay={handleOnOk}
        testID="IDScanCaptureTemplate-BottomSheet"
      >
        {processingStatus !== undefined && BottomSheetContent[processingStatus]}
      </BottomSheetOrOverlay>
    </>
  )
}
